import React, { FC, useEffect, useReducer } from 'react';

import { find, isEmpty, map } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useReportingService } from 'services/Reporting/useReportingService';

import { Form, FormAction } from 'components/DataEntry/Form';
import { formStyles } from 'containers/Reports/AllReports/Components/styles';
import { useReportsDashboard } from 'containers/Reports/ReportsDashboard/hooks/useReportsDashboard';
import { selectReportsDashboard } from 'containers/Reports/ReportsDashboard/selectors';

import { actions } from '../slice';

interface Props {
  currentRecord: Record<string, any>;
  setCurrentRecord: (value: Record<string, any>) => void;
  onClose: () => void;
  actionType: FormAction;
}

export const ReportingChartForm: FC<Props> = props => {
  const { currentRecord, onClose, actionType, setCurrentRecord } = props;
  const dispatch = useDispatch();
  const { updateCharts } = useSelector(selectReportsDashboard);
  const { getReportingCharts, allCharts } = useReportsDashboard();
  const { reportingCharts } = useReportingService();

  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      isEdit: false,
      name: allCharts[0]?.label ?? '',
      description: allCharts[0]?.label ?? '',
      chartType: allCharts[0],
      chartTypeOptions: [],
    },
  );

  useEffect(() => {
    updateState({ isEdit: actionType === 'Update' });
  }, [actionType]);

  //get list of charts which are not enabled on dashboard
  useEffect(() => {
    if (actionType === 'Add') {
      reportingCharts.sync({ dashboardFlag: false });
    }
  }, [reportingCharts, actionType]);

  //set chart type options
  useEffect(() => {
    updateState({
      chartTypeOptions: [
        {
          label: 'Chart Types',
          options: allCharts,
        },
        {
          label: 'Existing Charts',
          options: map(reportingCharts.data, o => ({
            label: o?.name,
            value: o?.chart_type + '_custom',
            type: 'custom',
            data: o,
          })),
        },
      ],
    });
  }, [reportingCharts.data, allCharts]);

  /*set chart type*/
  useEffect(() => {
    if (state.isEdit)
      updateState({
        chartType:
          find(
            state.chartTypeOptions[0]?.options,
            o => o.value === currentRecord?.chart_type,
          ) ?? {},
      });
  }, [state.chartTypeOptions, currentRecord, state.isEdit]);

  //set name
  useEffect(() => {
    currentRecord && state.isEdit && updateState({ name: currentRecord?.name });
  }, [currentRecord, state.isEdit]);

  useEffect(() => {
    name;
  }, []);

  const handleSubmit = data => {
    const { name, description, chartType } = data;
    const payload = {
      name,
      description,
      enable_on_dashboard: true,
    };
    const onSuccess = () => {
      getReportingCharts();
      onClose();
      setCurrentRecord({});
    };
    if (state.isEdit || !isEmpty(currentRecord)) {
      dispatch(
        actions.updateChart({
          q: {
            editChartRequest: payload as any,
            chartId: currentRecord?.id,
          },
          onSuccess,
        }),
      );
    } else {
      dispatch(
        actions.addChart({
          q: {
            addChartRequest: {
              ...payload,
              chart_type: chartType?.value,
            } as any,
          },
          onSuccess,
        }),
      );
    }
  };

  return (
    <Form
      schema={{
        chartType: {
          type: 'react-select',
          label: 'Select Chart Type',
          options: state.chartTypeOptions,
          isRequired: true,
          value: state.chartType,
          onChange: selected => {
            updateState({
              chartType: selected,
              name: selected?.label,
              description: selected?.label,
            });
            if (selected?.type === 'custom') setCurrentRecord(selected?.data);
          },
          isDisabled: state.isEdit || reportingCharts.isLoading,
          isLoading: reportingCharts.isLoading,
        },
        name: {
          type: 'text',
          label:
            'Chart Title' +
            (actionType === 'Add' ? ' (Edit Default As Needed)' : ''),
          isRequired: true,
          value: state.name,
          onChange: value => updateState({ name: value }),
        },
        description: {
          type: 'textArea',
          label: 'Description',
          value: state?.description,
          onChange: value => updateState({ description: value }),
        },
      }}
      styles={{
        ...formStyles,
        form: { ...formStyles.form, submitButton: { px: 6, border: 'none' } },
        textField: { input: { fontSize: '15px' } },
        textAreaField: { textArea: { h: 28, fontSize: '15px' } },
      }}
      buttonOptions={{
        submit: {
          name: 'OK',
          autoFocus: true,
          isDisabled: !state.chartType?.value || !state.name,
          isLoading: updateCharts.isLoading,
        },
        reset: {
          name: 'CANCEL',
          isVisible: true,
          onClick: onClose,
        },
      }}
      handleSubmit={handleSubmit}
    />
  );
};
